import { Component, OnDestroy, OnInit } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { LedgerService, RmsMessageService, RmsPulldownService } from '@services';
import { LedgerTransactionDetailComponent } from '../ledger-transaction-detail/ledger-transaction-detail.component';
import { dateOnly, exportLedgerExcel } from '../ledger-export';

const ALL = { value: '', label: 'All' };

@Component({
  selector: 'app-ledger-inquiry',
  imports: [RmsModule],
  templateUrl: './ledger-inquiry.component.html',
  styleUrl: './ledger-inquiry.component.scss'
})
export class LedgerInquiryComponent implements OnInit, OnDestroy {
  AccountOptions: { value: any; label: string }[] = [ALL];
  AccountTypeOptions: { value: any; label: string }[] = [ALL];
  SourceTypeOptions: { value: any; label: string }[] = [ALL];
  PaymentMethodOptions: { value: any; label: string }[] = [ALL];
  CustomerOptions: { value: any; label: string }[] = [ALL];
  StatusOptions: { value: any; label: string }[] = [ALL];
  DataRange: { FirstDate: string; LastDate: string } | null = null;

  Filters = {
    FromDate: '',
    ThroughDate: '',
    AccountID: '',
    AccountTypeID: '',
    SourceType: '',
    PaymentMethodID: '',
    CompanyID: '',
    Status: '',
    Reference: '',
    SearchText: ''
  };
  ReviewMode = false;

  Rows: any[] = [];
  Summary: any = null;
  Loading = false;
  private request = 0;
  private lastSearch: { filters: any; reviewMode: boolean } | null = null;

  Columns: Column[] = [
    { label: 'Entry', key: 'TransactionID', width: 7 },
    { label: 'Date', key: 'TransactionDate', type: 'date', width: 8 },
    { label: 'Account', key: 'AccountDisplay', width: 16 },
    { label: 'Source', key: 'SourceType', width: 10, SearchType: 'select' },
    { label: 'Customer / Payee', key: 'PartyDisplay', width: 15 },
    { label: 'Reference', key: 'ReferenceDisplay', width: 11 },
    { label: 'Method', key: 'MethodDisplay', width: 11, SearchType: 'select' },
    { label: 'Deposit', key: 'DepositDisplay', type: 'currency', width: 8, align: 'right' },
    { label: 'Withdrawal', key: 'WithdrawalDisplay', type: 'currency', width: 8, align: 'right' },
    { label: 'Balance', key: 'Balance', type: 'currency', width: 9, align: 'right' },
    { label: 'Status', key: 'Status', width: 7, SearchType: 'select' }
  ];

  Buttons: HeaderButton[] = [
    { label: 'Export', icon: 'download', class: 'btn-bg-blue', disabled: true, onAction: () => this.export() }
  ];

  constructor(private ledger: LedgerService, private message: RmsMessageService, private pulldown: RmsPulldownService) { }

  ngOnInit() {
    this.ledger.getFilterOptions().subscribe({
      next: (options) => {
        this.AccountOptions = [ALL, ...(options?.accounts || []).map((a: any) => ({ value: a.AccountID, label: `${a.AccountNumber} ${a.AccountName}` }))];
        this.AccountTypeOptions = [ALL, ...(options?.accountTypes || []).map((t: any) => ({ value: t.AccountTypeID, label: t.AccountType }))];
        this.PaymentMethodOptions = [ALL, ...(options?.paymentMethods || []).map((m: any) => ({ value: m.PaymentMethodID, label: m.MethodName }))];
        this.SourceTypeOptions = [ALL, ...(options?.sourceTypes || []).map((s: string) => ({ value: s, label: s }))];
        this.StatusOptions = [ALL, ...(options?.statuses || []).map((st: string) => ({ value: st, label: st }))];
        this.CustomerOptions = [ALL, ...(options?.customers || []).map((c: any) => ({ value: c.CompanyID, label: `${c.CompanyName} (${c.CompanyID})` }))];
        this.DataRange = options?.dataRange || null;
        this.setDefaultDates();
      },
      error: (err) => {
        this.setDefaultDates();
        this.message.error('Failed to load ledger filters: ' + this.errorText(err));
      }
    });
  }

  ngOnDestroy() { ++this.request; }

  // open on the last month that actually has data, not an empty current month
  private setDefaultDates() {
    const last = this.DataRange?.LastDate ? String(this.DataRange.LastDate).substring(0, 10) : new Date().toISOString().substring(0, 10);
    this.Filters.ThroughDate = last;
    this.Filters.FromDate = last.substring(0, 8) + '01';
  }

  toggleReviewMode(on: boolean) {
    this.ReviewMode = on;
    this.search();
  }

  clearFilters() {
    this.Filters = { ...this.Filters, AccountID: '', AccountTypeID: '', SourceType: '', PaymentMethodID: '', CompanyID: '', Status: '', Reference: '', SearchText: '' };
    this.ReviewMode = false;
  }

  search() {
    if (!this.ReviewMode) {
      if (!this.Filters.FromDate || !this.Filters.ThroughDate) { this.message.warn('Enter both From and Through dates.'); return; }
      if (this.Filters.ThroughDate < this.Filters.FromDate) { this.message.warn('Through date cannot be before From date.'); return; }
    }

    const filters = { ...this.Filters };
    const reviewMode = this.ReviewMode;
    const request = ++this.request;
    this.Loading = true;
    this.ledger.getInquiry({ ...filters, DateNeedsReview: reviewMode }).subscribe({
      next: (result) => {
        if (request !== this.request) return;
        this.Loading = false;
        this.Summary = result?.summary || null;
        this.Rows = (result?.rows || []).map((r: any) => this.toRow(r));
        this.lastSearch = { filters, reviewMode };
        this.Buttons[0].disabled = this.Rows.length === 0;
        this.Buttons = [...this.Buttons];
      },
      error: (err) => {
        if (request !== this.request) return;
        this.Loading = false;
        this.message.error('Ledger search failed: ' + this.errorText(err));
      }
    });
  }

  private toRow(r: any) {
    const refs = [
      r.JournalNumber ? r.JournalNumber : '',
      r.RoyalNYInvoiceNumber ? `Inv ${r.RoyalNYInvoiceNumber}` : '',
      r.MiscellaneousInvoiceNumber ? `Misc ${r.MiscellaneousInvoiceNumber}` : '',
      r.CheckNumber ? `Chk ${r.CheckNumber}` : ''
    ].filter(Boolean);
    const unresolved = r.PaymentMethodName === 'Legacy Unresolved';
    return {
      ...r,
      AccountDisplay: r.AccountNumber ? `${r.AccountNumber} ${r.AccountName}` : '—',
      PartyDisplay: r.CompanyName || r.PayorOrPayee || '—',
      ReferenceDisplay: refs.join(' · ') || '—',
      MethodDisplay: unresolved ? `Unresolved: ${r.LegacyPaymentText || 'code ' + r.LegacyModeOfPayment}` : (r.PaymentMethodName || '—'),
      DepositDisplay: r.DepositAmount ? r.DepositAmount : null,
      WithdrawalDisplay: r.WithdrawalAmount ? r.WithdrawalAmount : null
    };
  }

  openTransaction(row: any) {
    if (!row?.TransactionID) return;
    this.pulldown.open(LedgerTransactionDetailComponent, {
      title: 'Ledger Entry #' + row.TransactionID,
      data: { TransactionID: row.TransactionID },
      width: '80vw',
      maxWidth: '95vw',
      maxHeight: '92vh',
      top: '0'
    });
  }

  get FilterSummary(): string {
    const s = this.lastSearch;
    if (!s) return '';
    const f = s.filters;
    const label = (options: { value: any; label: string }[], value: any) => options.find(o => String(o.value) === String(value))?.label;
    const parts = [
      s.reviewMode ? 'Dates needing review' : `${f.FromDate} to ${f.ThroughDate}`,
      f.AccountID ? 'Account: ' + label(this.AccountOptions, f.AccountID) : '',
      f.AccountTypeID ? 'Account type: ' + label(this.AccountTypeOptions, f.AccountTypeID) : '',
      f.SourceType ? 'Source: ' + f.SourceType : '',
      f.PaymentMethodID ? 'Method: ' + label(this.PaymentMethodOptions, f.PaymentMethodID) : '',
      f.CompanyID ? 'Customer: ' + label(this.CustomerOptions, f.CompanyID) : '',
      f.Status ? 'Status: ' + f.Status : '',
      f.Reference ? 'Reference: ' + f.Reference : '',
      f.SearchText ? 'Search: ' + f.SearchText : ''
    ];
    return parts.filter(Boolean).join(' | ');
  }

  async export() {
    if (!this.Rows.length || !this.Summary) return;
    const s = this.Summary;
    await exportLedgerExcel({
      title: 'Ledger Inquiry',
      fileName: 'ledger-inquiry',
      filterSummary: this.FilterSummary,
      generated: s.GeneratedAtUtc ? new Date(s.GeneratedAtUtc) : new Date(),
      summary: [
        ['Opening balance', s.OpeningBalance, true],
        ['Deposits', s.PeriodDeposits, true],
        ['Withdrawals', s.PeriodWithdrawals, true],
        ['Closing balance', s.ClosingBalance, true],
        ['Records', s.RecordCount]
      ],
      columns: [
        { header: 'Entry', value: r => r.TransactionID, width: 10 },
        { header: 'Date', value: r => dateOnly(r.TransactionDate), width: 12 },
        { header: 'Account', value: r => r.AccountDisplay, width: 30 },
        { header: 'Source', value: r => r.SourceType, width: 16 },
        { header: 'Customer / Payee', value: r => r.PartyDisplay, width: 28 },
        { header: 'Reference', value: r => r.ReferenceDisplay, width: 22 },
        { header: 'Method', value: r => r.MethodDisplay, width: 26 },
        { header: 'Deposit', value: r => r.DepositAmount || null, width: 14, money: true },
        { header: 'Withdrawal', value: r => r.WithdrawalAmount || null, width: 14, money: true },
        { header: 'Balance', value: r => r.Balance, width: 16, money: true },
        { header: 'Status', value: r => r.Status, width: 12 },
        { header: 'Date needs review', value: r => r.DateNeedsReview ? 'Yes' : '', width: 16 }
      ],
      rows: this.Rows
    });
  }

  private errorText(err: any): string {
    return err?.error?.error || err?.message || 'Unknown error';
  }
}
